<template>
  <div class="container mt-4 mb-5">
    <b-tabs content-class="mt-4" fill class="d-block w-100">
      <b-tab title="Schweizer Guides" active>
        <div class="card shadow-sm mb-4 w-100 mt-3">
          <div class="card-header bg-warning text-white">
            <h2 class="card-title mb-0">Schweizer Guides</h2>
          </div>
          <div class="card-body">
            <textarea v-model="customMessage" rows="5" class="form-control mb-4"
              placeholder="Enter custom message here..."></textarea>

            <div class="file-upload mb-4">
              <input type="file" class="form-control-file mb-3" ref="fileInput" @change="handleFileUpload"
                accept=".xlsx, .xls" />

              <div class="mb-3">
                <button class="btn btn-success mr-3" @click="downloadZip" :disabled="!isFileUploaded">
                  <b-spinner v-if="isProcessingZip" small type="grow"></b-spinner>
                  Download ZIP
                </button>
                <button class="btn btn-secondary" @click="sendAllEmails" :disabled="!isFileUploaded">
                  <b-spinner v-if="isSendingEmails" small type="grow"></b-spinner>
                  Send Emails
                </button>
              </div>

              <div class="progress mt-3" v-if="showProgressBar">
                <div class="progress-bar progress-bar-striped bg-success" role="progressbar"
                  :style="{ width: progress + '%' }" :aria-valuenow="progress" aria-valuemin="0" aria-valuemax="100">
                  {{ progress }}%
                </div>
              </div>
            </div>

            <div v-if="inconsistentPNs.length > 0" class="alert alert-danger mt-3 text-left">
              <strong>Warning!</strong> The following PN numbers have inconsistent email addresses:
              <ul class="mb-0 mt-2">
                <li v-for="pn in inconsistentPNs" :key="pn">{{ pn }}</li>
              </ul>
            </div>

            <div v-if="logs.length > 0" class="alert alert-info mt-3 text-left">
              <strong>Logs:</strong>
              <ul class="mb-0 mt-2 list-unstyled">
                <li v-for="log in logs" :key="log" :class="getLogClass(log)">
                  {{ log }}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </b-tab>

      <b-tab title="Ocean Guides">
        <div class="card shadow-sm mb-4 w-100 mt-3">
          <div class="card-header bg-info text-white">
            <h2 class="card-title mb-0">Ocean Guides</h2>
          </div>
          <div class="card-body">
            <div class="file-upload mb-4">
              <input type="file" class="form-control-file mb-3" ref="fileInputOcean" @change="handleFileUploadOcean"
                accept=".xlsx, .xls" />

              <div class="mb-3" v-if="availableMonthsOcean.length > 0">
                <label for="monthSelectOcean">Select Month:</label>
                <select id="monthSelectOcean" v-model="selectedMonthOcean" class="form-control w-25">
                  <option v-for="month in availableMonthsOcean" :key="month" :value="month">{{ month }}</option>
                </select>
              </div>

              <div class="mb-3">
                <button class="btn btn-success mr-3" @click="downloadZipOcean" :disabled="!isFileUploadedOcean">
                  <b-spinner v-if="isProcessingZipOcean" small type="grow"></b-spinner>
                  Download ZIP
                </button>
                <b-button class="btn btn-success" to="/sproof">Upload to Sproof</b-button>
              </div>

              <div class="progress mt-3" v-if="showProgressBarOcean">
                <div class="progress-bar progress-bar-striped bg-success" role="progressbar"
                  :style="{ width: progressOcean + '%' }" :aria-valuenow="progressOcean" aria-valuemin="0"
                  aria-valuemax="100">
                  {{ progressOcean }}%
                </div>
              </div>
            </div>

            <div v-if="inconsistentPNsOcean.length > 0" class="alert alert-danger mt-3 text-left">
              <strong>Warning!</strong> The following PN numbers have inconsistent email addresses:
              <ul class="mb-0 mt-2">
                <li v-for="pn in inconsistentPNsOcean" :key="pn">{{ pn }}</li>
              </ul>
            </div>

            <div v-if="logsOcean.length > 0" class="alert alert-info mt-3 text-left">
              <strong>Logs:</strong>
              <ul class="mb-0 mt-2 list-unstyled">
                <li v-for="log in logsOcean" :key="log" :class="getLogClass(log)">
                  {{ log }}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </b-tab>

      <b-tab title="Agenda Activation">
        <div class="card shadow-sm mb-4 w-100 mt-3">
          <div class="card-header bg-primary text-white">
            <h2 class="card-title mb-0">Agenda Activation</h2>
          </div>
          <div class="card-body">
            <div class="mb-3">
              <label class="font-weight-bold">Subject</label>
              <input type="text" v-model="agendaSubject" class="form-control" />
            </div>
            <div class="mb-3">
              <label class="font-weight-bold">Message</label>
              <textarea v-model="agendaMessage" rows="8" class="form-control"
                placeholder="Enter custom message here..."></textarea>
            </div>

            <div class="file-upload mb-4">
              <input type="file" class="form-control-file mb-3" ref="fileInputAgenda" @change="handleFileUploadAgenda"
                accept=".pdf" multiple />

              <div class="mb-3">
                <button class="btn btn-secondary" @click="sendAgendaEmails" :disabled="!isAgendaFilesUploaded">
                  <b-spinner v-if="isSendingAgendaEmails" small type="grow"></b-spinner>
                  Send Emails
                </button>
              </div>

              <div class="progress mt-3" v-if="showAgendaProgressBar">
                <div class="progress-bar progress-bar-striped bg-success" role="progressbar"
                  :style="{ width: agendaProgress + '%' }" :aria-valuenow="agendaProgress" aria-valuemin="0" aria-valuemax="100">
                  {{ agendaProgress }}%
                </div>
              </div>
            </div>

            <!-- UI to show selected files and their extracted emails -->
            <div v-if="agendaFiles.length > 0" class="mt-3">
              <h5>Selected Files</h5>
              <div class="table-responsive">
                <table class="table table-sm table-bordered">
                  <thead class="bg-light">
                    <tr>
                      <th>File Name</th>
                      <th>Recipient Email</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(file, index) in agendaFiles" :key="index">
                      <td class="align-middle">{{ file.file.name }}</td>
                      <td>
                        <input type="email" v-model="file.email" class="form-control form-control-sm" placeholder="user@domain.com" />
                      </td>
                      <td class="align-middle">
                        <span v-if="file.status === 'sent'" class="text-success"><i class="fa fa-check"></i> Sent</span>
                        <span v-else-if="file.status === 'failed'" class="text-danger"><i class="fa fa-times"></i> Failed</span>
                        <span v-else class="text-muted">Pending</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div v-if="logsAgenda.length > 0" class="alert alert-info mt-3 text-left">
              <strong>Logs:</strong>
              <ul class="mb-0 mt-2 list-unstyled">
                <li v-for="log in logsAgenda" :key="log" :class="getLogClass(log)">
                  {{ log }}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </b-tab>
    </b-tabs>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import * as XLSX from 'xlsx';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import moment from 'moment';
import { generatePDF } from '../utils/pdfGeneratorswizz';
import { generatePDF as generateOceanPDF } from '../utils/pdfGeneratorOcean';
import axios from 'axios';
import * as pdfjsLib from 'pdfjs-dist';

// Use hardcoded version to prevent local webpack dev-server cache from causing issues
pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@2.16.105/build/pdf.worker.min.js';

const customMessage = ref(`Guten Tag,
Anbei senden wir Dir die Übersicht, der mit der aktuellen Lohnzahlung erfassten und abgerechneten Einsätze.

Beste Grüße
`);

const jsonData = ref(null);
const isFileUploaded = ref(false);
const showProgressBar = ref(false);
const progress = ref(0);
const fileInput = ref(null);
const isProcessingZip = ref(false);
const isSendingEmails = ref(false);
const inconsistentPNs = ref([]);
const logs = ref([]);

const jsonDataOcean = ref(null);
const isFileUploadedOcean = ref(false);
const showProgressBarOcean = ref(false);
const progressOcean = ref(0);
const fileInputOcean = ref(null);
const isProcessingZipOcean = ref(false);
const inconsistentPNsOcean = ref([]);
const logsOcean = ref([]);
const availableMonthsOcean = ref([]);
const selectedMonthOcean = ref('');

const agendaSubject = ref('Agenda Aktivierung');
const agendaMessage = ref(`Hey,

Einmal der Code zur Freischaltung des Agenda Portals. Du solltest eine Mail an deine PRIVATE E-Mail-Adresse bekommen haben.

Bei Fragen gerne melden.

Your TCSL Accounting Team

LG`);
const fileInputAgenda = ref(null);
const isAgendaFilesUploaded = ref(false);
const isSendingAgendaEmails = ref(false);
const showAgendaProgressBar = ref(false);
const agendaProgress = ref(0);
const agendaFiles = ref([]);
const logsAgenda = ref([]);

// Helper Functions
const getLogClass = (log) => (log.includes('PDF not generated') ? 'text-danger' : '');
const arrayBufferToBase64 = (buffer) => window.btoa(String.fromCharCode(...new Uint8Array(buffer)));

// Function to get the highest month from date strings
const getHighestMonth = (dateStrings) => {
  const formats = ['DD.MM.YYYY', 'M/D/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD', 'D/M/YYYY', 'DD/MM/YYYY', 'M/D/YY'];
  const germanMonths = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

  const validDates = dateStrings.map(dateStr => moment(dateStr, formats, true)).filter(date => date.isValid()).sort((a, b) => b - a);
  const highestDate = validDates[0];

  return highestDate ? `${germanMonths[highestDate.month()]} ${highestDate.year()}` : 'Ungültiges Datum';
};

// File Upload Handler
const handleFileUpload = (event) => {
  const file = event.target.files[0];
  if (file) {
    isProcessingZip.value = true;
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array', sheetRows: 10000 });
      const worksheet = workbook.Sheets['Einsätze'];
      if (worksheet) {
        let decodedRange = XLSX.utils.decode_range(worksheet['!ref']);
        if (decodedRange.e.r > 10000) {
          decodedRange.e.r = 10000;
        }
        if (!worksheet['A1']) {
          decodedRange.s.r = Math.max(decodedRange.s.r, 1);
        }
        const restrictedRange = XLSX.utils.encode_range(decodedRange);

        jsonData.value = XLSX.utils.sheet_to_json(worksheet, { range: restrictedRange, raw: false, cellFormula: false });
        inconsistentPNs.value = checkForInconsistentEmails(jsonData.value);
        isFileUploaded.value = true;
        isProcessingZip.value = false;
      } else {
        alert("Sheet 'Einsätze' not found!");
        isProcessingZip.value = false;
      }
    };
    reader.readAsArrayBuffer(file);
  }
};

// Check for inconsistent emails
const checkForInconsistentEmails = (data) => {
  const pnEmails = data.reduce((acc, curr) => {
    if (!acc[curr.PN]) acc[curr.PN] = new Set();
    acc[curr.PN].add(curr.Email);
    return acc;
  }, {});
  return Object.keys(pnEmails).filter(pn => pnEmails[pn].size > 1);
};

// Email Sending Function
const sendEmail = async (recipientEmail, subject, message, attachment, logArray = logs) => {
  const accessToken = localStorage.getItem('authToken');
  if (!accessToken) {
    logArray.value.push(`Error: Access token is missing for ${recipientEmail}. Please login again.`);
    return false;
  }

  const emailData = {
    message: {
      subject,
      body: { contentType: "Text", content: message },
      toRecipients: [{ emailAddress: { address: recipientEmail } }],
      attachments: [attachment],
    },
  };

  try {
    await axios.post('https://graph.microsoft.com/v1.0/me/sendMail', emailData, {
      headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
    });
    logArray.value.push(`Email sent successfully to ${recipientEmail}`);
    return true; // Email sent successfully
  } catch (error) {
    const errorMessage = error.response
      ? `Error sending email to ${recipientEmail}: Token is invalid or expired, please logout and login again with accountingtcs@time-change.services`
      : `Error sending email to ${recipientEmail}`;

    logArray.value.push(errorMessage); // Log the error message to logs
    return false; // Email failed to send
  }
};

// Function to Generate PDFs and Download as ZIP
const generatePDFsAndZip = async (data) => {
  const zip = new JSZip();
  let processedItems = 0;
  const uniquePNs = [...new Set(data.map(item => item.PN))];

  for (const pn of uniquePNs) {
    const hostData = data.filter(item => item.PN === pn);
    if (hostData.length > 0) {
      const host = {
        Name: hostData[0].Name,
        First: hostData[0].First,
        PN: hostData[0].PN,
        Office: hostData[0].Office,
        Email: hostData[0].Email,
        projects: hostData,
      };

      if (host.Email === '0') {
        logs.value.push(`Skipping host: ${host.Name} ${host.First} due to Email being '0'`);
        continue;
      }

      const pdfData = await generatePDF(host);
      if (pdfData) {
        const highestMonth = getHighestMonth(host.projects.filter(project => parseFloat(project.Delta) !== 0).map(project => project['Date ']));
        const fileName = `${host.Name}_${host.PN}_Lohnabrechnung_${highestMonth}.pdf`;
        zip.file(fileName, pdfData);
      } else {
        logs.value.push(`PDF not generated for: ${host.Name} ${host.First}`);
      }
    }
    processedItems++;
    progress.value = Math.round((processedItems / uniquePNs.length) * 100);
    await new Promise(resolve => setTimeout(resolve, 0));
  }
  return zip.generateAsync({ type: 'blob' });
};

// Download ZIP Handler
const downloadZip = async () => {
  if (jsonData.value) {
    progress.value = 0;
    showProgressBar.value = true;
    isProcessingZip.value = true;
    const zipContent = await generatePDFsAndZip(jsonData.value);
    saveAs(zipContent, 'Guides.zip');
    showProgressBar.value = false;
    isProcessingZip.value = false;
    resetFileInput();
  }
};

// Send Emails Handler
const sendAllEmails = async () => {
  if (jsonData.value) {
    progress.value = 0;
    showProgressBar.value = true;
    isSendingEmails.value = true;
    logs.value = []; // Reset logs before sending emails

    const uniquePNs = [...new Set(jsonData.value.map(item => item.PN))];
    let emailsSent = 0;

    for (const pn of uniquePNs) {
      const hostData = jsonData.value.filter(item => item.PN === pn);
      if (hostData.length > 0) {
        const host = {
          Name: hostData[0].Name,
          First: hostData[0].First,
          PN: hostData[0].PN,
          Office: hostData[0].Office,
          Email: hostData[0].Email,
          projects: hostData,
        };

        if (host.Email === '0') {
          logs.value.push(`Skipping host: ${host.Name} ${host.First} due to Email being '0'`);
          continue;
        }

        const pdfData = await generatePDF(host);
        if (pdfData) {
          const highestMonth = getHighestMonth(host.projects.filter(project => parseFloat(project.Delta) !== 0).map(project => project['Date ']));
          const fileName = `${host.Name}_${host.PN}_Lohnabrechnung_${highestMonth}.pdf`;
          const pdfBase64 = arrayBufferToBase64(pdfData);
          const attachment = {
            "@odata.type": "#microsoft.graph.fileAttachment",
            name: fileName,
            contentBytes: pdfBase64,
          };

          const personalizedMessage = customMessage.value.replace(/{Name}/g, host.Name);
          const emailSent = await sendEmail(host.Email, "Your Payroll PDF", personalizedMessage, attachment);

          // Update progress bar
          emailsSent++;
          progress.value = Math.round((emailsSent / uniquePNs.length) * 100);
        } else {
          logs.value.push(`PDF not generated for: ${host.Name} ${host.First}`);
        }
      }
      await new Promise(resolve => setTimeout(resolve, 0));
    }

    showProgressBar.value = false;
    isSendingEmails.value = false;
  }
};

const handleFileUploadOcean = (event) => {
  const file = event.target.files[0];
  if (file) {
    isProcessingZipOcean.value = true;
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array', sheetRows: 10000 });
      const worksheet = workbook.Sheets['Einsätze'];
      if (worksheet) {
        let decodedRange = XLSX.utils.decode_range(worksheet['!ref']);
        if (decodedRange.e.r > 10000) {
          decodedRange.e.r = 10000;
        }
        if (!worksheet['A1']) {
          decodedRange.s.r = Math.max(decodedRange.s.r, 1);
        }
        const restrictedRange = XLSX.utils.encode_range(decodedRange);

        jsonDataOcean.value = XLSX.utils.sheet_to_json(worksheet, { range: restrictedRange, raw: false, cellFormula: false });
        
        const months = new Set();
        console.log("First row from excel:", jsonDataOcean.value[0]);
        let monthKey = null;
        if (jsonDataOcean.value.length > 0) {
          const keys = Object.keys(jsonDataOcean.value[0]);
          monthKey = keys.find(key => {
            const normalized = key.trim().toLowerCase();
            return normalized === 'month' || normalized === 'months';
          });
          console.log("Found month key:", monthKey);
        }
        
        jsonDataOcean.value.forEach(row => {
          // Check possible column names
          const monthVal = monthKey ? row[monthKey] : (row.Months || row['Months '] || row.Month || row['Month ']);
          if (monthVal) months.add(monthVal);
        });
        const monthOrder = {
          january: 1, januar: 1, jan: 1,
          february: 2, februar: 2, feb: 2,
          march: 3, märz: 3, maerz: 3, mar: 3,
          april: 4, apr: 4,
          may: 5, mai: 5,
          june: 6, juni: 6, jun: 6,
          july: 7, juli: 7, jul: 7,
          august: 8, aug: 8,
          september: 9, sep: 9,
          october: 10, oktober: 10, oct: 10,
          november: 11, nov: 11,
          december: 12, dezember: 12, dec: 12, dez: 12
        };

        availableMonthsOcean.value = Array.from(months)
          .filter(Boolean)
          .sort((a, b) => {
            const valA = monthOrder[a.toLowerCase().trim()] || 99;
            const valB = monthOrder[b.toLowerCase().trim()] || 99;
            return valA - valB;
          });
        if (availableMonthsOcean.value.length > 0) {
          selectedMonthOcean.value = availableMonthsOcean.value[0];
        }

        inconsistentPNsOcean.value = checkForInconsistentEmails(jsonDataOcean.value);
        isFileUploadedOcean.value = true;
        isProcessingZipOcean.value = false;
      } else {
        alert("Sheet 'Einsätze' not found!");
        isProcessingZipOcean.value = false;
      }
    };
    reader.readAsArrayBuffer(file);
  }
};



const generatePDFsAndZipOcean = async (data) => {
  const zip = new JSZip();
  let processedItems = 0;
  const uniquePNs = [...new Set(data.map(item => item.PN))];

  for (const pn of uniquePNs) {
    const hostData = data.filter(item => item.PN === pn);
    if (hostData.length > 0) {
      const host = {
        Name: hostData[0].Name,
        First: hostData[0].First,
        PN: hostData[0].PN,
        Office: hostData[0].Office,
        Email: hostData[0].Email,
        projects: hostData,
      };

      if (host.Email === '0') {
        logsOcean.value.push(`Skipping host: ${host.Name} ${host.First} due to Email being '0'`);
        continue;
      }

      const pdfData = await generateOceanPDF(host, selectedMonthOcean.value);
      if (pdfData) {
        const fileName = `${host.Name}_${host.PN}_Arbeitszeitnachweis_${selectedMonthOcean.value || 'unknown'}.pdf`;
        zip.file(fileName, pdfData);
      } else {
        logsOcean.value.push(`PDF not generated for: ${host.Name} ${host.First}`);
      }
    }
    processedItems++;
    progressOcean.value = Math.round((processedItems / uniquePNs.length) * 100);
    await new Promise(resolve => setTimeout(resolve, 0));
  }
  return zip.generateAsync({ type: 'blob' });
};

const downloadZipOcean = async () => {
  if (jsonDataOcean.value) {
    progressOcean.value = 0;
    showProgressBarOcean.value = true;
    isProcessingZipOcean.value = true;
    const zipContent = await generatePDFsAndZipOcean(jsonDataOcean.value);
    saveAs(zipContent, 'OceanGuides.zip');
    showProgressBarOcean.value = false;
    isProcessingZipOcean.value = false;
    fileInputOcean.value.value = '';
    isFileUploadedOcean.value = false;
  }
};



const resetFileInput = () => {
  fileInput.value.value = '';
  isFileUploaded.value = false;
};

// Agenda handlers
const handleFileUploadAgenda = async (event) => {
  const files = event.target.files;
  if (files && files.length > 0) {
    const newFiles = [];
    isAgendaFilesUploaded.value = false;
    
    for (const file of files) {
      let email = '';
      try {
        const arrayBuffer = await file.arrayBuffer();
        const loadingTask = pdfjsLib.getDocument({ data: new Uint8Array(arrayBuffer) });
        const pdf = await loadingTask.promise;
        let fullText = '';
        
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const textContent = await page.getTextContent();
          const pageText = textContent.items.map(item => item.str).join(' ');
          fullText += pageText + ' ';
        }
        
        // Match standard email format
        const emailMatch = fullText.match(/([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/);
        if (emailMatch) {
          email = emailMatch[1];
        }
      } catch (error) {
        console.error("Error extracting text from PDF:", error);
        logsAgenda.value.push(`Could not read text from ${file.name}: ${error.message || error}`);
      }
      
      newFiles.push({ file, email, status: 'pending' });
    }
    
    agendaFiles.value = newFiles;
    isAgendaFilesUploaded.value = true;
  } else {
    agendaFiles.value = [];
    isAgendaFilesUploaded.value = false;
  }
};

const sendAgendaEmails = async () => {
  if (agendaFiles.value.length === 0) return;
  
  showAgendaProgressBar.value = true;
  isSendingAgendaEmails.value = true;
  agendaProgress.value = 0;
  logsAgenda.value = [];
  
  let emailsSent = 0;
  const totalFiles = agendaFiles.value.length;

  for (let i = 0; i < totalFiles; i++) {
    const item = agendaFiles.value[i];
    if (!item.email) {
      logsAgenda.value.push(`Skipping ${item.file.name}: No email address provided.`);
      item.status = 'failed';
      continue;
    }

    try {
      const arrayBuffer = await item.file.arrayBuffer();
      const pdfBase64 = arrayBufferToBase64(arrayBuffer);
      
      const attachment = {
        "@odata.type": "#microsoft.graph.fileAttachment",
        name: item.file.name,
        contentBytes: pdfBase64,
        contentType: "application/pdf"
      };

      const emailSent = await sendEmail(item.email, agendaSubject.value, agendaMessage.value, attachment, logsAgenda);
      
      if (emailSent) {
        item.status = 'sent';
      } else {
        item.status = 'failed';
      }
    } catch (error) {
      logsAgenda.value.push(`Error processing ${item.file.name}`);
      item.status = 'failed';
    }
    
    emailsSent++;
    agendaProgress.value = Math.round((emailsSent / totalFiles) * 100);
  }
  
  isSendingAgendaEmails.value = false;
};
</script>



<style scoped>
.alert {
  margin-top: 20px;
}

.card-title {
  font-size: 24px;
  font-weight: bold;
}
</style>
